/**
 * StorageMeter — Admin widget showing the live MongoDB Atlas storage footprint.
 * Uses `storageSize + indexSize` (the actual on-disk usage Atlas counts against
 * the M0 512 MB free-tier limit). Lets the admin purge stale GridFS uploads
 * with a single click after migrating to URL-only media.
 */
import { useCallback, useEffect, useState } from "react";
import { fetchStorage } from "@/api";
import { client } from "@/api";
import { RefreshCw, HardDrive, AlertTriangle, Trash2 } from "lucide-react";
import { toast } from "sonner";

const fmtMB = (mb) => `${mb.toFixed(2)} MB`;
const fmtBytes = (b) => {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
};

export default function StorageMeter() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purging, setPurging] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await fetchStorage();
      setData(d);
    } catch (e) {
      toast.error("Failed to load storage stats");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function purgeUploads() {
    // Fetch the latest stats first so we always purge based on real numbers.
    let current;
    try {
      current = await fetchStorage();
      setData(current);
    } catch {
      toast.error("Could not refresh storage stats");
      return;
    }
    const files = current?.gridfs_files || 0;
    const mb = current?.gridfs_mb || 0;
    if (!files) {
      toast.info("No uploaded files to purge.");
      return;
    }
    if (!window.confirm(
      `Delete ALL ${files} uploaded file${files === 1 ? "" : "s"} (${mb.toFixed(2)} MB)?\n\n` +
      `Only do this if every product/portfolio image is already hosted externally (imgur/postimg/etc). ` +
      `Any product still using a /api/files/... URL will break.`
    )) return;
    setPurging(true);
    try {
      const r = await client.post("/admin/storage/purge-gridfs");
      toast.success(`Purged ${r.data.files_deleted} files — freed ${r.data.mb_freed} MB`);
      const d = await fetchStorage();
      setData(d);
    } catch (e) {
      toast.error("Purge failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setPurging(false);
    }
  }

  async function reclaimSpace() {
    if (!window.confirm(
      "Drop ALL empty upload collections to reclaim disk space?\n\n" +
      "Use this if you've already purged uploads but the storage bar is still high. " +
      "MongoDB doesn't release deleted-document space until the collection is dropped."
    )) return;
    setPurging(true);
    try {
      const r = await client.post("/admin/storage/reclaim");
      const dropped = r.data.collections_dropped || [];
      if (dropped.length === 0) {
        toast.info("Nothing to reclaim — no empty upload collections.");
      } else {
        toast.success(`Reclaimed: dropped ${dropped.join(", ")}`);
      }
      const d = await fetchStorage();
      setData(d);
    } catch (e) {
      toast.error("Reclaim failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setPurging(false);
    }
  }

  if (loading && !data) {
    return (
      <div className="border border-[var(--theme-border)] bg-[var(--theme-surface)] p-5">
        <div className="font-mono text-[10px] text-[var(--theme-text-muted)] uppercase tracking-widest">
          Loading storage stats…
        </div>
      </div>
    );
  }
  if (!data) return null;

  const used = data.used_mb || 0;
  const limit = data.limit_mb || 512;
  const pct = Math.min((used / limit) * 100, 100);
  const isCritical = pct >= 80;
  const isWarning = pct >= 60 && !isCritical;
  const barColor = isCritical
    ? "#ff3548"
    : isWarning
    ? "#ffaa00"
    : "var(--theme-accent-primary)";

  // Sort collections by storage size descending
  const cols = Object.entries(data.collections || {})
    .map(([name, c]) => ({ name, ...c }))
    .sort((a, b) => (b.storage_size_bytes || 0) - (a.storage_size_bytes || 0));
  const topCol = cols.slice(0, 8); // top 8 to keep UI tight

  return (
    <div
      data-testid="storage-meter"
      className="border border-[var(--theme-border)] bg-[var(--theme-surface)] p-6 space-y-5"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <HardDrive size={16} className="text-[var(--theme-accent-primary)]" />
          <div>
            <div className="font-mono text-[10px] text-[var(--theme-text-muted)] uppercase tracking-widest">
              [ STORAGE_USAGE ]
            </div>
            <div className="font-display text-lg md:text-xl font-bold uppercase tracking-tight">
              Database Capacity
            </div>
          </div>
        </div>
        <button
          data-testid="storage-refresh"
          onClick={load}
          disabled={loading}
          className="text-[var(--theme-text-muted)] hover:text-[var(--theme-text)] transition disabled:opacity-40"
          aria-label="refresh storage"
          title="Refresh"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {/* Big progress bar */}
      <div>
        <div className="flex items-baseline justify-between mb-2 gap-2 flex-wrap">
          <span className="font-display text-3xl md:text-4xl font-extrabold tabular-nums">
            {fmtMB(used)}
          </span>
          <span className="font-mono text-[11px] text-[var(--theme-text-muted)] tabular-nums">
            of {limit} MB ({pct.toFixed(1)}%)
          </span>
        </div>
        <div className="h-2 bg-[var(--theme-border)] rounded-full overflow-hidden">
          <div
            data-testid="storage-bar"
            className="h-full transition-[width] duration-500 rounded-full"
            style={{ width: `${pct}%`, background: barColor }}
          />
        </div>
        {isCritical && (
          <div className="mt-2 flex items-center gap-1.5 text-[11px] text-red-400 font-mono uppercase tracking-wider">
            <AlertTriangle size={12} />
            Approaching limit — contact support@emergent.sh to upgrade.
          </div>
        )}
        {isWarning && (
          <div className="mt-2 flex items-center gap-1.5 text-[11px] text-yellow-400 font-mono uppercase tracking-wider">
            <AlertTriangle size={12} />
            Over 60% used — plan to upgrade or optimize images soon.
          </div>
        )}
      </div>

      {/* Breakdown */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2 border-t border-[var(--theme-border)]">
        <Stat label="Documents" value={fmtMB(Math.max(0, data.data_size_mb - data.gridfs_mb))} />
        <Stat
          label="Media (GridFS)"
          value={fmtMB(data.gridfs_mb)}
          sub={`${data.gridfs_files} file${data.gridfs_files === 1 ? "" : "s"}`}
        />
        <Stat label="Indexes" value={fmtMB(data.index_size_mb)} />
        <Stat label="On-disk (compressed)" value={fmtMB(data.storage_size_mb)} />
      </div>

      {/* Per-collection table */}
      {topCol.length > 0 && (
        <div className="pt-2 border-t border-[var(--theme-border)]">
          <div className="font-mono text-[10px] text-[var(--theme-text-muted)] uppercase tracking-widest mb-2">
            // Top collections by storage
          </div>
          <div className="overflow-hidden border border-[var(--theme-border)]">
            <table className="w-full font-mono text-[11px]">
              <thead>
                <tr className="bg-[var(--theme-bg)] text-[var(--theme-text-muted)]">
                  <th className="text-left py-1.5 px-3 font-normal">Collection</th>
                  <th className="text-right py-1.5 px-3 font-normal">Docs</th>
                  <th className="text-right py-1.5 px-3 font-normal">Logical</th>
                  <th className="text-right py-1.5 px-3 font-normal">On-disk</th>
                </tr>
              </thead>
              <tbody>
                {topCol.map((c) => (
                  <tr key={c.name} className="border-t border-[var(--theme-border)]">
                    <td className="py-1.5 px-3 text-[var(--theme-text)]">{c.name}</td>
                    <td className="py-1.5 px-3 text-right tabular-nums">{c.count}</td>
                    <td className="py-1.5 px-3 text-right tabular-nums text-[var(--theme-text-muted)]">{fmtBytes(c.size_bytes)}</td>
                    <td className="py-1.5 px-3 text-right tabular-nums">{fmtBytes(c.storage_size_bytes)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Reclaim button — show whenever empty upload collections still hold disk */}
      {(() => {
        const uploadCols = cols.filter(
          (c) => c.name.startsWith("uploads.") && c.count === 0 && c.storage_size_bytes > 0
        );
        if (uploadCols.length === 0) return null;
        const total = uploadCols.reduce((s, c) => s + (c.storage_size_bytes || 0), 0);
        return (
          <div className="pt-2 border-t border-[var(--theme-border)] flex items-center justify-between gap-4 flex-wrap">
            <div className="font-mono text-[11px] text-[var(--theme-text-muted)] leading-relaxed flex-1 min-w-[200px]">
              <span className="text-red-400">⚠ DISK NOT RELEASED.</span> Empty <span className="text-white">{uploadCols.map((c) => c.name).join(", ")}</span> collections still hold <span className="text-white">{fmtBytes(total)}</span> of disk space. MongoDB doesn&apos;t release deleted-doc space until the collection is dropped. Click <span className="text-[var(--theme-accent-primary)]">RECLAIM SPACE</span> below.
            </div>
            <button
              data-testid="storage-reclaim"
              onClick={reclaimSpace}
              disabled={purging}
              className="border border-yellow-500/60 text-yellow-400 hover:bg-yellow-500 hover:text-black px-4 py-2 font-mono text-[10px] uppercase tracking-widest flex items-center gap-1.5 transition disabled:opacity-50 whitespace-nowrap"
            >
              <Trash2 size={12} />
              {purging ? "Working…" : "Reclaim Space"}
            </button>
          </div>
        );
      })()}

      {/* Purge button — only show if there are GridFS files */}
      {data.gridfs_files > 0 && (
        <div className="pt-2 border-t border-[var(--theme-border)] flex items-center justify-between gap-4 flex-wrap">
          <div className="font-mono text-[11px] text-[var(--theme-text-muted)] leading-relaxed flex-1 min-w-[200px]">
            <span className="text-yellow-400">⚠</span> You have <span className="text-white">{data.gridfs_files} file{data.gridfs_files === 1 ? "" : "s"}</span> still in GridFS using <span className="text-white">{fmtMB(data.gridfs_mb)}</span>. Since you switched to URL-only media, you can purge them to reclaim space — but ONLY if no product/portfolio still references a <code className="text-[var(--theme-accent-primary)]">/api/files/...</code> URL.
          </div>
          <button
            data-testid="storage-purge"
            onClick={purgeUploads}
            disabled={purging}
            className="border border-red-500/60 text-red-400 hover:bg-red-500 hover:text-white px-4 py-2 font-mono text-[10px] uppercase tracking-widest flex items-center gap-1.5 transition disabled:opacity-50 whitespace-nowrap"
          >
            <Trash2 size={12} />
            {purging ? "Purging…" : "Purge Uploaded Files"}
          </button>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div>
      <div className="font-mono text-[9px] text-[var(--theme-text-muted)] uppercase tracking-widest">
        {label}
      </div>
      <div className="font-display text-base font-bold tabular-nums">{value}</div>
      {sub && <div className="font-mono text-[9px] text-[var(--theme-text-muted)]">{sub}</div>}
    </div>
  );
}
